import { useState, useEffect } from 'react';
import { getApiBaseUrl, Hair_API_PREFIX } from '../../utils/apiConfig';
import { fetchWithTimeout } from '../../utils/fetchWithTimeout';
import { buildClientRoutineShowcase, isDefaultDummyProduct } from '../../utils/hairReportUtils';
import {
  fetchHairTreatmentRecommendations,
  normalizeRecommendGender,
} from '../../utils/hairTreatmentRecommendationsApi';

export function useHairReportData({ sessionId, token, hairResults, userData, initialDbRecommendations }) {
  const [dbRecommendations] = useState(() => initialDbRecommendations || {});
  const [recsLoading] = useState(false);
  const [routineData, setRoutineData] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [routineLoading, setRoutineLoading] = useState(true);
  const [recommendedTreatments, setRecommendedTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(true);
  const [treatmentsError, setTreatmentsError] = useState('');

  useEffect(() => {
    const apiBaseUrl = getApiBaseUrl();
    let cancelled = false;

    const fetchRoutine = async () => {
      if (!sessionId) {
        setRoutineLoading(false);
        return;
      }

      setRoutineLoading(true);
      try {
        const response = await fetchWithTimeout(
          `${apiBaseUrl}${Hair_API_PREFIX}/get-stored-routine/${encodeURIComponent(sessionId)}?app_session_token=${encodeURIComponent(token || '')}`,
          { timeoutMs: 15000 }
        );
        const data = await response.json();
        if (!cancelled && data.success && data.routine) {
          setRoutineData(data.routine);
          const showcase = (data.recommended_products?.showcase || [])
            .filter((p) => !isDefaultDummyProduct(p));
          if (showcase.length > 0) {
            setRecommendedProducts(showcase);
          } else {
            const morning = (data.recommended_products?.morning || []).filter((p) => !isDefaultDummyProduct(p));
            const evening = (data.recommended_products?.evening || []).filter((p) => !isDefaultDummyProduct(p));
            setRecommendedProducts(buildClientRoutineShowcase(morning, evening, hairResults));
          }
        }
      } catch (e) {
        console.error('Routine fetch failed', e);
      } finally {
        if (!cancelled) setRoutineLoading(false);
      }
    };

    void fetchRoutine();

    return () => {
      cancelled = true;
    };
  }, [sessionId, token, hairResults]);

  useEffect(() => {
    let cancelled = false;
    if (!hairResults) {
      setTreatmentsLoading(false);
      return undefined;
    }
    setTreatmentsLoading(true);
    setTreatmentsError('');
    const gender = normalizeRecommendGender(userData?.gender);
    void fetchHairTreatmentRecommendations(hairResults, { token, limit: 6, gender })
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.recommended_treatments) ? data.recommended_treatments : [];
        setRecommendedTreatments(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setRecommendedTreatments([]);
          setTreatmentsError(err?.message || 'Could not load recommended services');
        }
      })
      .finally(() => {
        if (!cancelled) setTreatmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hairResults, token, userData?.gender]);

  return {
    dbRecommendations,
    recsLoading,
    routineData,
    recommendedProducts,
    routineLoading,
    recommendedTreatments,
    treatmentsLoading,
    treatmentsError,
    isLoading: routineLoading,
  };
}
